'use client';
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deriveDimNonActiveOpacityByConstellationKey, deriveLinkedActiveMapFromActivationLinks } from '../../_pure/itemActivation.js';
import { resolveConnectorConstellationRenderPlan } from '../_pure/connectorRenderPlan.js';
import { flattenItemParametricState } from '../_pure/spaceRendererHelpers.js';
import { createGpuResourceCache } from '../gpuResourceCache.js';
import { ConnectorConstellationRenderer } from './ConnectorConstellationRenderer.js';
import { EntityConstellationRenderer } from './EntityConstellationRenderer.js';
import { RingPulse } from './RingPulse.js';
import { SceneLighting } from './SceneLighting.js';
function SpaceRenderer(props) {
    const { derivationFrame } = props;
    const [hoveredEntity, setHoveredEntity] = useState(null);
    const itemsByCollectionKey = useMemo(() => new Map(derivationFrame.itemCollections
        .filter((itemCollection) => itemCollection.itemCollectionKind === 'entity')
        .map((itemCollection) => [itemCollection.itemCollectionKey, itemCollection.items])), [derivationFrame.itemCollections]);
    const connectorItemsByCollectionKey = useMemo(() => new Map(derivationFrame.itemCollections
        .filter((itemCollection) => itemCollection.itemCollectionKind === 'connector')
        .map((itemCollection) => [itemCollection.itemCollectionKey, itemCollection.items])), [derivationFrame.itemCollections]);
    const entityConstellations = useMemo(() => derivationFrame.constellations.filter((constellation) => constellation.constellationKind === 'entity'), [derivationFrame.constellations]);
    const connectorConstellations = useMemo(() => derivationFrame.constellations.filter((constellation) => constellation.constellationKind === 'connector'), [derivationFrame.constellations]);
    const effectiveActiveItemKeyByConstellationKey = useMemo(() => {
        const activeItemKeyByConstellationKey = derivationFrame.spaceRenderOptions?.activation?.activeItemKeyByConstellationKey ?? {};
        if (hoveredEntity === null)
            return activeItemKeyByConstellationKey;
        return {
            ...activeItemKeyByConstellationKey,
            [hoveredEntity.constellationKey]: hoveredEntity.itemKey,
        };
    }, [derivationFrame.spaceRenderOptions?.activation?.activeItemKeyByConstellationKey, hoveredEntity]);
    const effectiveLinkedActiveItemKeysByConstellationKey = useMemo(() => {
        return deriveLinkedActiveMapFromActivationLinks({
            directActiveItemKeyByConstellationKey: effectiveActiveItemKeyByConstellationKey,
            activationLinks: derivationFrame.spaceRenderOptions?.activation?.activationLinks,
        });
    }, [
        derivationFrame.spaceRenderOptions?.activation?.activationLinks,
        effectiveActiveItemKeyByConstellationKey,
    ]);
    const entityConstellationParametricStateByKey = useMemo(() => derivationFrame.constellationParametricState?.entityConstellationByKey ?? {}, [derivationFrame.constellationParametricState]);
    const connectorConstellationParametricStateByKey = useMemo(() => derivationFrame.constellationParametricState?.connectorConstellationByKey ?? {}, [derivationFrame.constellationParametricState]);
    const dimNonActiveOpacityByConstellationKey = useMemo(() => {
        return deriveDimNonActiveOpacityByConstellationKey({
            entityConstellations,
            entityConstellationParametricStateByKey,
            dimming: derivationFrame.spaceRenderOptions?.activation?.nonActiveItemDimming,
            effectiveActiveItemKeyByConstellationKey,
            effectiveLinkedActiveItemKeysByConstellationKey,
        });
    }, [
        entityConstellations,
        entityConstellationParametricStateByKey,
        derivationFrame.spaceRenderOptions,
        effectiveActiveItemKeyByConstellationKey,
        effectiveLinkedActiveItemKeysByConstellationKey,
    ]);
    const effectiveItemParametricStateByKey = useMemo(() => {
        return flattenItemParametricState(derivationFrame.itemParametricState);
    }, [derivationFrame.itemParametricState]);
    const resourceCache = useMemo(() => createGpuResourceCache(), []);
    useEffect(() => {
        return () => {
            resourceCache.dispose();
        };
    }, [resourceCache]);
    useEffect(() => {
        if (hoveredEntity === null)
            return;
        const hoveredCollection = entityConstellations.find((constellation) => constellation.constellationKey === hoveredEntity.constellationKey);
        if (!hoveredCollection) {
            setHoveredEntity(null);
            return;
        }
        const hoveredItems = itemsByCollectionKey.get(hoveredCollection.itemCollectionKey) ?? [];
        const hoveredItemStillExists = hoveredItems.some((item) => item.itemKey === hoveredEntity.itemKey);
        if (!hoveredItemStillExists) {
            setHoveredEntity(null);
        }
    }, [entityConstellations, hoveredEntity, itemsByCollectionKey]);
    const emitItemIntentImpl = (intent) => {
        const behaviorController = derivationFrame.itemBehaviorController;
        if (!behaviorController)
            return;
        if (intent.constellationKey === null || intent.itemKey === null) {
            behaviorController.onIntent(intent);
            return;
        }
        const sourceConstellation = entityConstellations.find((constellation) => constellation.constellationKey === intent.constellationKey);
        if (!sourceConstellation)
            return;
        const sourceItems = itemsByCollectionKey.get(sourceConstellation.itemCollectionKey) ?? [];
        const sourceItem = sourceItems.find((item) => item.itemKey === intent.itemKey);
        if (!sourceItem)
            return;
        const sourceItemParametricState = effectiveItemParametricStateByKey[sourceItem.itemKey];
        if (sourceItemParametricState?.interaction?.enabled === false)
            return;
        behaviorController.onIntent(intent);
    };
    const emitItemIntentRef = useRef(emitItemIntentImpl);
    emitItemIntentRef.current = emitItemIntentImpl;
    // Stable identity so memoized constellations can be reused; always dispatches against the latest frame.
    const emitItemIntent = useCallback((intent) => emitItemIntentRef.current(intent), []);
    const renderEntityConstellation = (constellation) => {
        if (constellation.constellationKind !== 'entity')
            return null;
        return (_jsx(EntityConstellationRenderer, { constellation: constellation, context: {
                entityItems: itemsByCollectionKey.get(constellation.itemCollectionKey) ?? [],
                entityConstellations,
                entityItemsByCollectionKey: itemsByCollectionKey,
                spaceRenderOptions: derivationFrame.spaceRenderOptions,
                resourceCache,
                hoveredEntity,
                setHoveredEntity,
                emitItemIntent,
                effectiveActiveItemKeyByConstellationKey,
                effectiveLinkedActiveItemKeysByConstellationKey,
                itemParametricStateByKey: effectiveItemParametricStateByKey,
                entityConstellationParametricStateByKey,
                dimNonActiveOpacityByConstellationKey,
            } }, constellation.constellationKey));
    };
    return (_jsxs(_Fragment, { children: [props.lighting === false ? null
                : _jsx(SceneLighting, { ...(props.spotlight ? { spotlight: props.spotlight } : {}) }), Object.entries(derivationFrame.itemParametricState?.entityItemByKey ?? {}).map(([key, state]) => state?.ringPulse ? _jsx(RingPulse, { pulse: state.ringPulse }, key) : null), derivationFrame.constellations.map((constellation) => renderEntityConstellation(constellation)), connectorConstellations.map((constellation) => (_jsx(ConnectorConstellationRenderer, { resourceCache: resourceCache, ...(props.readCueElapsed ? { readCueElapsed: props.readCueElapsed } : {}), plan: resolveConnectorConstellationRenderPlan({
                    constellation,
                    items: connectorItemsByCollectionKey.get(constellation.itemCollectionKey) ?? [],
                    connectorConstellationParametricStateByKey,
                    anchors: {
                        entityConstellations,
                        entityItemsByCollectionKey: itemsByCollectionKey,
                        itemParametricStateByKey: effectiveItemParametricStateByKey,
                        entityConstellationParametricStateByKey,
                    },
                    hoveredEntity,
                    effectiveActiveItemKeyByConstellationKey,
                    effectiveLinkedActiveItemKeysByConstellationKey,
                }) }, constellation.constellationKey)))] }));
}
export default memo(SpaceRenderer);
